<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type {
    UsageProfileWeekDay,
    UsageProfileOpeningHours,
} from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            heading: "Horaires hebdomadaires",
            edit: "Modifier",
            open: "Ouvert",
            closed: "Fermé",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Profil de la semaine (horaires par jour) */
        weekProfile: UsageProfileWeekDay[]
        /** Mode lecture seule (cache le bouton d'édition) */
        readonly?: boolean
    }>(),
    { readonly: false }
)

defineEmits<{ "click:edit": [] }>()

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Transforme les horaires d'ouverture en chaîne formatée
 */
function transformScheduleHoursToString(
    scheduleHours: UsageProfileOpeningHours[] | undefined
): string {
    if (!scheduleHours?.length) return ""
    return scheduleHours.reduce((acc, time, index) => {
        const tmp = `${time.debut} - ${time.fin}`
        if (index === 0) return tmp
        if (index === scheduleHours.length - 1) return `${acc} et ${tmp}`
        return `${acc}, ${tmp}`
    }, "")
}

/** Lignes formatées pour l'affichage (jour + valeur) */
const getLines = computed(() =>
    props.weekProfile.map((line) => ({
        label: line.jour?.charAt(0).toUpperCase() + line.jour?.slice(1),
        value: line.ouvert
            ? t("open")
            : line.ferme
              ? t("closed")
              : transformScheduleHoursToString(line.horaires_ouverture),
    }))
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT -->
    <!-- ================================ -->
    <div class="usage-profile-item-week">
        <UsageProfileItemField
            class="usage-profile-item-week__header min-h-10 px-3 border-b border-gray-200"
            :label="t('heading')"
            label-class="text-primary-700! text-base! font-semibold!"
        >
            <BaseButton
                v-if="!readonly"
                :label="t('edit')"
                size="small"
                text
                @click="$emit('click:edit')"
            />
        </UsageProfileItemField>
        <UsageProfileItemField
            v-for="(line, index) in getLines"
            :key="index"
            class="usage-profile-item-week__line border-b border-gray-200 hover:bg-primary-100"
            :value="line.value"
            :label="line.label"
        />
    </div>
</template>
